/**
 * `dispatch.*` wire contract (garment Increment 8, `garment.dispatch`).
 *
 * Build a packing list + shipping docs, then confirm to draft the export invoice
 * (`sales/invoices`) and ship the style through the QC gate. Behind
 * `entitledProcedureFor("garment.dispatch")`.
 */
import { z } from "zod";
import { uuid, isoDate } from "../_common.js";

export const exportRoute = z.enum([
  "export_lut",
  "export_with_igst",
  "taxable_igst",
  "taxable_cgst_sgst",
]);

const paiseString = z.string().regex(/^\d+$/, "integer paise as a string");
const pcsPerCarton = z.number().int().min(1).max(100_000);

const shippingDocs = {
  shippingBillNo: z.string().trim().max(40).nullable().optional(),
  shippingBillDate: isoDate.nullable().optional(),
  blAwbNo: z.string().trim().max(60).nullable().optional(),
  vesselOrFlight: z.string().trim().max(80).nullable().optional(),
  portOfLoading: z.string().trim().max(80).nullable().optional(),
  destination: z.string().trim().max(120).nullable().optional(),
  cha: z.string().trim().max(120).nullable().optional(),
  containerRef: z.string().trim().max(60).nullable().optional(),
};

export const getForStyleInput = z.object({ styleId: uuid });

export const createDraftInput = z.object({
  styleId: uuid,
  dispatchDate: isoDate,
  pcsPerCarton,
  packedQty: z.number().int().min(0).max(10_000_000).nullable().optional(),
  notes: z.string().trim().max(2000).nullable().optional(),
  ...shippingDocs,
});
export const createDraftOutput = z.object({ id: uuid });

export const updateInput = z.object({
  dispatchId: uuid,
  dispatchDate: isoDate.optional(),
  pcsPerCarton: pcsPerCarton.optional(),
  packedQty: z.number().int().min(0).max(10_000_000).nullable().optional(),
  notes: z.string().trim().max(2000).nullable().optional(),
  ...shippingDocs,
});
export const updateOutput = z.object({ id: uuid });

export const confirmInput = z.object({
  dispatchId: uuid,
  taxTreatment: exportRoute.optional(),
  fxRateToInrPaise: paiseString.optional(),
  dueOn: isoDate.optional(),
  overrideReason: z.string().trim().max(500).nullable().optional(),
});
export const confirmOutput = z.object({
  id: uuid,
  styleStatus: z.string(),
  invoiceId: uuid,
});

const dispatchRow = z.object({
  id: uuid,
  status: z.enum(["draft", "confirmed"]),
  dispatchDate: isoDate,
  pcsPerCarton: z.number().int(),
  cartonsTotal: z.number().int(),
  packedQty: z.number().int(),
  shippingBillNo: z.string().nullable(),
  shippingBillDate: z.string().nullable(),
  blAwbNo: z.string().nullable(),
  vesselOrFlight: z.string().nullable(),
  portOfLoading: z.string().nullable(),
  destination: z.string().nullable(),
  cha: z.string().nullable(),
  containerRef: z.string().nullable(),
  taxTreatment: z.string().nullable(),
  fxRateToInrPaise: z.string().nullable(),
  invoiceId: uuid.nullable(),
  notes: z.string().nullable(),
  createdAt: z.string().datetime(),
});

export const dispatchView = z.object({
  styleId: uuid,
  styleStatus: z.string(),
  orderQty: z.number().int().nonnegative(),
  currency: z.string(),
  hsn: z.string(),
  rosctlEligible: z.boolean(),
  rosctlRateBps: z.number().int().nonnegative(),
  dispatch: dispatchRow.nullable(),
  packing: z
    .object({
      pcsPerCarton: z.number().int(),
      cartonsTotal: z.number().int(),
      cells: z.array(z.object({ colour: z.string(), size: z.string(), pieces: z.number().int() })),
    })
    .nullable(),
  qcBlocked: z.boolean(),
  canDispatch: z.boolean(),
});
export const getForStyleOutput = dispatchView;

export type DispatchViewWire = z.infer<typeof dispatchView>;
